import { useState } from "react";
import { ApiResponse } from "apisauce";
import { toast } from "react-toastify";
import { CheckBulkUpdateResponse } from "../interface/api/CheckBulkUpdateResponse";
import { validateTicketRange } from "../utils/ticket-helpers";
import { BulkOperationMaxRange } from "../utils/constants";

interface BulkUpdateService<TPayload> {
  checkBulkUpdate: (payload: TPayload) => Promise<ApiResponse<any>>;
  bulkUpdate: (payload: TPayload) => Promise<ApiResponse<any>>;
}

export function useBulkUpdateFlow<TPayload extends { from: number; to: number }>({
  service,
  buildPayload,
  onSuccess,
  setPending,
}: {
  service: BulkUpdateService<TPayload>;
  buildPayload: (values: any) => TPayload;
  onSuccess: () => void;
  setPending: (v: boolean) => void;
}) {
  const [checkDialogOpen, setCheckDialogOpen] = useState(false);
  const [checkValues, setCheckValues] = useState<TPayload | null>(null);
  const [checkResponse, setCheckResponse] = useState<CheckBulkUpdateResponse>();
  const [checkResetForm, setCheckResetForm] = useState<(() => void) | null>(null);

  const onUpdate = async (values: any, resetForm: () => void) => {
    // Bulk update keeps the old 2,000 cap — unlike generate and export, this endpoint
    // has not been reworked to avoid loading the whole table.
    const range = validateTicketRange(values.from, values.to, BulkOperationMaxRange);
    if (!range) return;

    const payload = buildPayload({ ...values, from: range.from, to: range.to });

    const checkResp = await service.checkBulkUpdate(payload);
    if (!checkResp.ok) {
      toast.error("Error checking ticket updates");
      return;
    }

    setCheckValues(payload);
    setCheckResponse(checkResp.data as CheckBulkUpdateResponse);
    setCheckResetForm(() => resetForm);
    setCheckDialogOpen(true);
  };

  const onConfirmUpdate = async () => {
    if (!checkValues) return;
    setPending(true);

    toast.info(`Updating tickets from ${checkValues.from} to ${checkValues.to}`);

    const response: ApiResponse<any> = await service.bulkUpdate(checkValues);
    if (response.ok) {
      toast.success(`${response.data.updated} ticket(s) updated`);
      if (checkResetForm) checkResetForm();
      onSuccess();
    } else {
      toast.error("Failed to update tickets");
    }
    setPending(false);
    setCheckDialogOpen(false);
    setCheckValues(null);
    setCheckResponse(undefined);
  };

  const onCloseCheckDialog = () => {
    setCheckDialogOpen(false);
    setCheckValues(null);
    setCheckResponse(undefined);
  };

  return {
    checkDialogOpen,
    checkResponse,
    onUpdate,
    onConfirmUpdate,
    onCloseCheckDialog,
  };
}
